import * as reduxOffline from './reduxOffline';
import getHttpHeaders from '../http/getHttpHeaders';
import { getActiveTenantId, getActiveUseCaseId, scopeHeaders } from './activeScope';

/**
 *
 * @param {import('../eventSink').EventSink } eventSink
 * @returns
 */
export async function initialize(eventSink, session) {
    return reduxOffline.initialize(eventSink, session);
}

/**
 *
 * @param  {...any} args
 * @example
 * {
        type: 'persist_X_Y',
        persist: {...}
        meta: {
            offline: {
                effect: {
                    type: 'update'
                    url: 'https://app.sstid.com/.....',
                    patch: [...]
                    newModel: {...}
                    oldModel: {...}
                },
                commit: {
                    type: `submit_X_Y_COMMIT`,
                    persist: {...}
                },
                rollback: {
                    type: `submit_X_Y_ROLLBACK`,
                    persist: {...}
                }
            },
        },
    }
 * @returns
 */
export function sendWithOfflineResilience(...args) {
    return reduxOffline.process(...args);
}

/**
 *
 * @returns {{ busy:boolean, online:boolean, pendingCount:number, retryCount:number, retryScheduled:boolean }}
 */
export function getStatus() {
    return reduxOffline.getStatus();
}

export function getOfflineAction(meta, correlationId, _id, payload, context) {
    // Gather the information for redux-offline action
    const { namespace, relation, verb } = context;
    const path = `/api/${namespace}/${relation}/${_id}`;
    const commitRollbackVerb = verb === 'remove' ? 'remove' : 'submit';
    // Absent means "queued before these stamps existed" to the queue, so record each only when it
    // is known. The use case is recorded as well as the tenant because the server reads both off
    // the token of whichever request finally sends this, and a sibling use case of the same tenant
    // resolves different unique-key fields, feature flags and repository overrides.
    const tenantId = getActiveTenantId() ?? undefined;
    const useCaseId = getActiveUseCaseId() ?? undefined;
    // Naming the scope on the request itself is what actually holds this write to it. The queue
    // also compares the ids above against the active scope before releasing a write, but without
    // these headers the server is told the tenant the session holds, which another tab can change.
    const httpHeaders = { ...getHttpHeaders({ ...meta, correlationId }), ...scopeHeaders({ tenantId, useCaseId }) };
    const actionPayload = { ...payload, httpHeaders, path };
    // redux-offline hands the *commit/rollback* action to dequeue, not the queued action, so the
    // two need a shared id for dequeue to remove the item peek actually sent.
    const offlineActionId = correlationId;
    // Create redux action syntax for offline resilience
    return {
        type: `${verb}_${namespace}_${relation}`,
        [verb]: actionPayload,
        meta: {
            offlineActionId,
            tenantId,
            useCaseId,
            offline: {
                effect: actionPayload,
                commit: {
                    type: `${commitRollbackVerb}_${namespace}_${relation}_commit`,
                    meta: { offlineActionId },
                    [verb]: actionPayload
                },
                rollback: {
                    type: `${commitRollbackVerb}_${namespace}_${relation}_rollback`,
                    meta: { offlineActionId },
                    [verb]: actionPayload
                }
            }
        }
    };
}

export function enable(enable) {
    reduxOffline.enable(enable);
}

export default { sendWithOfflineResilience, getStatus, getOfflineAction, initialize, enable };
